<script setup lang="ts">
import { extractCoverAccent } from '../lib/coverAccent';

const props = defineProps<{
  title: string;
  src?: string;
}>();

const emit = defineEmits<{
  accent: [value: string];
}>();

// Falls back to the CRE magazine proportions until the cover reports its own
const DEFAULT_RATIO = 438 / 600;

const coverRef = useTemplateRef<HTMLImageElement>('cover');
const ratio = ref(DEFAULT_RATIO);

/**
 * A cover on file that does not load — a missing file, or a PDF picked as the
 * cover — leaves the blank book instead of the browser's broken icon and alt text
 */
const isBroken = ref(false);

watch(
  () => props.src,
  () => {
    isBroken.value = false;
  },
);

const applyNaturalRatio = () => {
  const image = coverRef.value;

  if (!image?.naturalWidth || !image.naturalHeight) {
    return;
  }

  ratio.value = image.naturalWidth / image.naturalHeight;

  const accent = extractCoverAccent(image);

  if (accent) {
    emit('accent', accent);
  }
};

// Covers already in the browser cache never fire load, and a cover that failed
// before hydration never fires error for the listener either
onMounted(() => {
  const image = coverRef.value;

  if (!image?.complete) return;

  if (image.naturalWidth) {
    applyNaturalRatio();
  } else {
    isBroken.value = true;
  }
});
</script>

<template>
  <div :class="$style.wrapper" :style="{ '--cover-ratio': ratio }">
    <div :class="$style.book">
      <div :class="$style.front">
        <img
          v-if="src && !isBroken"
          ref="cover"
          :src="src"
          :alt="title"
          :class="$style.image"
          @load="applyNaturalRatio"
          @error="isBroken = true"
        />
        <div v-else :class="$style.placeholder">
          <span v-if="!src" :class="$style.placeholderTitle">{{ title }}</span>
        </div>
      </div>
      <div :class="$style.paper" />
      <div :class="$style.back" />
    </div>
    <div :class="$style.shadow" />
  </div>
</template>

<style module lang="scss">
/*
 * The box the cover is fitted into. On its own it takes the proportions of the
 * cover itself; a caller showing several at once — the grid — hands it one
 * shared ratio through --cover-box-ratio so every cover comes out the same
 * height and the titles below line up
 */
.wrapper {
  --thickness: 16px;

  position: relative;
  width: 100%;
  aspect-ratio: var(--cover-box-ratio, var(--cover-ratio));
  perspective: 1500px;
}

/*
 * Taken out of the flow so the box keeps the height its ratio asks for: the
 * book then fills that height, takes its width from the proportions of the
 * cover, and the auto margins centre it in whatever width is left over
 */
.book {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2;
  width: auto;
  max-width: 100%;
  height: 100%;
  margin: 0 auto;
  aspect-ratio: var(--cover-ratio);
  transform-style: preserve-3d;
  transform: rotateY(-14deg);
  transition: transform 1s ease;
}

.front {
  position: absolute;
  inset: 0;
  border-radius: 4px;
  overflow: hidden;
  transform: translateZ(var(--thickness));
  backface-visibility: hidden;
}

.image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.placeholder {
  display: flex;
  align-items: flex-end;
  width: 100%;
  height: 100%;
  padding: var(--space-2-5x);
  background: linear-gradient(
    160deg,
    var(--color-gray-line) 0%,
    var(--color-gray-2) 100%
  );
}

.placeholderTitle {
  @include text_body2_medium;
  color: var(--color-gray-text);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.paper {
  position: absolute;
  top: 1%;
  right: 0;
  width: calc(var(--thickness) * 2);
  height: 98%;
  background:
    linear-gradient(90deg, rgba(0, 0, 0, 0.3), rgba(255, 255, 255, 0)),
    repeating-linear-gradient(
      90deg,
      #ffffff 0,
      #e2e2e2 1px,
      #ffffff 3px,
      #9a9a9a 4px
    );
  transform: rotateY(90deg) translateX(calc(var(--thickness) / 7))
    translateZ(calc(var(--thickness) / 1.2));
}

.back {
  position: absolute;
  inset: 0;
  background: var(--color-gray-2);
  border-radius: 4px;
  transform: translateZ(calc(-1 * var(--thickness)));
}

.shadow {
  position: absolute;
  z-index: 1;
  left: 50%;
  // Proportions follow the pen; rotateZ matches the book angle so the patch
  // stays under the cover instead of sliding out to the left
  // Half the height plus clearance: rotateX(90deg) pulls the band up towards
  // the vanishing point, and a smaller offset hides it behind the cover
  bottom: -21%;
  width: 92%;
  height: 26%;
  background: radial-gradient(
    70% 85% at 50% 50%,
    rgba(0, 0, 0, 0.2) 0%,
    rgba(0, 0, 0, 0) 100%
  );
  filter: blur(20px);
  transform: translateX(-50%) rotateX(90deg) rotateZ(14deg);
  transition: transform 1s ease;
}

.wrapper:hover {
  .book {
    transform: rotateX(5deg) rotateY(-24deg);
  }

  .shadow {
    transform: translateX(-50%) rotateX(90deg) rotateZ(24deg);
  }
}
</style>
